import { AfterViewChecked, Component, OnDestroy, OnInit } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {forkJoin, Subject} from 'rxjs';
import { BenefitsService } from '../../../../core/service/benefits.service';
import { GetFlexibleBenefitsRequest } from '../../../../core/requests/GetFlexibleBenefitsRequest';
import { takeUntil } from 'rxjs/operators';
import { GetFlexibleBenefitsResponse } from '../../../../core/responses/GetFlexibleBenefitsResponse';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { FlexibleBenefit } from '../../../../core/models/flexible-benefit';
import {
  CompanyTaxDistribution,
  AcquisitionType, GetProviderName, BenefitType, INITIAL_LOAD_CNT, LOAD_MORE_CNT,
} from '../../../../core/helpers/constants';
import { NgbCarouselConfig } from '@ng-bootstrap/ng-bootstrap';
import { AuthenticationService, GoogleAnalyticsService } from '../../../../core/service';
import { User } from '../../../../core/models';
import { BenefitDetailsService } from 'app/core/service/benefit-details.service';
import { BenefitHelperService } from 'app/core/service/benefit-helper.service';
import { SubdomainInfo } from 'app/core/models/subdomain-info';
import {GetProviderResponse} from "../../../../core/responses/GetProviderResponse";
import {Provider} from "../../../../core/models/provider";
import { DashboardService } from '../../../../core/service/dashboard.service';
import { EventModel } from '../../../../core/models/EventModel';

const THREE_PILLAR_CUSTOM_DESCRIPTION: string =
  'Colecția de vouchere cu facilități fiscale îți oferă beneficii speciale în momente importante din an, cum ar fi Paștele. Poți comanda vouchere de pana la 300 de lei neimpozabili si scutiti de la plata contributiilor sociale. Este un mod simplu și avantajos de a sărbători această ocazie specială alături de cei dragi! Pentru Paște, beneficiarii cadourilor cu facilități fiscale sunt: toți angajații.';

@Component({
  selector: 'app-flexible-supplier-info',
  templateUrl: './flexible-supplier-info.component.html',
  styleUrls: ['./flexible-supplier-info.component.scss'],
  providers: [TranslatePipe, NgbCarouselConfig],
})
export class FlexibleSupplierInfoComponent implements OnInit, OnDestroy, AfterViewChecked {
  // Scroll-management variables
  private static savedProviderId: number | undefined = undefined;
  private static savedEventId: number | undefined = undefined;
  private static savedScrollPosition = 0;
  private savedFilterScrollPosition = 0;
  private shouldRestoreScroll = false;

  private ngUnsubscribe = new Subject<void>();
  public cards = [];
  public card = {
    id: 0,
    title: '',
    src: '',
    description: '',
    providerName: '',
    price: 0,
  };
  public providerId: number;
  public ACQUISITION_TYPE = AcquisitionType;
  public subdomain: string;
  public queryParams: any;
  public selectedQuantity = 1;
  public CompanyTaxDistribution = CompanyTaxDistribution;
  public userCompanyTaxDistribution: number;
  public userDashboardInfo: User;
  public provider: Provider;
  public userCompanyInfo: SubdomainInfo;
  private initialBenefitsNotFiltered: boolean = false;
  loadedBenefits: any[] = [];
  totalBenefits: number = 0;
  benefitsOutdated: boolean = false;
  unconfirmedFilters: any = {};
  appliedFilters: any = {};
  displayedBenefits: number;
  companyId: number;
  dataLoadingDone = false;

  // Event mode fields
  public isEventMode = false;
  public eventId: number;
  public activeEvent: EventModel;

  constructor(
    private benefitsService: BenefitsService,
    private authenticationService: AuthenticationService,
    private _route: ActivatedRoute,
    private router: Router,
    private benefitDetailsService: BenefitDetailsService,
    private benefitHelper: BenefitHelperService,
    private gAnalyticsService: GoogleAnalyticsService,
    private dashboardService: DashboardService,
    config: NgbCarouselConfig
  ) {
    this.userCompanyTaxDistribution = this.authenticationService.currentUserInfoValue.companyTaxDistribution;
    this.companyId = this.authenticationService.currentUserInfoValue.companyId;
    config.wrap = false;
  }

  ngOnInit(): void {
    this.authenticationService.currentUserInfo
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((x) => {
        this.userDashboardInfo = x;
    });
    this.authenticationService.currentUserCompanyInfo
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((companyInfo) => {
        this.userCompanyInfo = companyInfo;
      });

    // Subscribe to route parameter changes to handle navigation to different suppliers
    this._route.params
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        const rawEventId = this._route.snapshot.params.eventId;
        const newEventId = parseInt(rawEventId, 10);
        this.isEventMode = !isNaN(newEventId) && rawEventId !== undefined;
        const newProviderId = parseInt(this._route.snapshot.params.providerId, 10);
        this.subdomain = this._route.snapshot.params['subdomain'];
        this.queryParams = this._route.snapshot.queryParams;
        this.selectedQuantity = 1;

        // Check if provider or event changed - if so, reload all data
        const providerOrEventChanged = (this.isEventMode && newEventId !== this.eventId) ||
                                       (!this.isEventMode && newProviderId !== this.providerId);

        if (providerOrEventChanged) {
          // Update the IDs first
          this.providerId = newProviderId;
          this.eventId = newEventId;
          
          // Clear previous data for the new provider
          this.loadedBenefits = [];
          this.totalBenefits = 0;
          this.cards = [];
          this.provider = undefined;
          this.dataLoadingDone = false;
          
          // Trigger data reload for the new provider
          this.initAppliedFilters();
          this.loadProviderAndBenefits();
        } else {
          // First time initialization
          this.providerId = newProviderId;
          this.eventId = newEventId;
          
          if (!this.dataLoadingDone) {
            this.initialBenefitsNotFiltered = this.initAppliedFilters();

            const cachedFiltersKey = this.benefitsService.getProviderBenefitsCachedFilterKey(this.companyId, this.providerId);
            let isSameProviderOrEvent: boolean;
            if (this.isEventMode) {
              isSameProviderOrEvent = this.eventId === FlexibleSupplierInfoComponent.savedEventId;
            } else {
              isSameProviderOrEvent = this.providerId === FlexibleSupplierInfoComponent.savedProviderId;
            }
            if (this.benefitsService.checkIfSameAppliedFilters(this.appliedFilters, cachedFiltersKey) === false || !isSameProviderOrEvent) {
              FlexibleSupplierInfoComponent.savedScrollPosition = 0;
            }

            this.loadProviderAndBenefits();
          }
        }
      });
  }

  private loadProviderAndBenefits(): void {
    if (this.isEventMode) {
      this.loadEventMode();
    } else {
      forkJoin({
        providerResponse: this.getProviderById(this.providerId),
        flexibleBenefitsResponse: this.getFlexibleBenefits()
      }).pipe(takeUntil(this.ngUnsubscribe))
        .subscribe({
          next: ({providerResponse, flexibleBenefitsResponse}) => {
            this.onGetProviderByIdSuccess(providerResponse);
            this.onGetFlexibleBenefitsSuccess(flexibleBenefitsResponse);

            this.dataLoadingDone = true;
            this.restoreScroll();
          },
          error: error => {
            this.onGetProviderByIdFail(error);
            this.onFlexibleBenefitsFail(error);

            this.dataLoadingDone = true;
          }
        });
    }
  }

  ngAfterViewChecked(): void {
    // Required to prevent scroll jumping to top on filter change
    // The scroll will be restored in the next tick after the view is updated with the new benefits.
    // TODO: Maybe find a better solution than this
    if (this.shouldRestoreScroll && this.savedFilterScrollPosition) {
      window.scrollTo(0, this.savedFilterScrollPosition);
      this.shouldRestoreScroll = false;
    }
  }

  public ngOnDestroy() {
    FlexibleSupplierInfoComponent.savedProviderId = this.providerId;
    FlexibleSupplierInfoComponent.savedEventId = this.eventId;
    FlexibleSupplierInfoComponent.savedScrollPosition = window.scrollY;
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  // ─── Event specific methods ──────────────────────────────────────────────────────────────

  public loadEventMode() {
    const hasGiftQuota = (this.authenticationService.currentUserInfoValue?.numberOfGiftTickets ?? 0) > 0;
    if (!hasGiftQuota) {
      this.dataLoadingDone = true;
      this.isEventMode = false;
      return;
    }

    this.dashboardService.getCompanyEvent(this.subdomain, this.eventId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: response => {
          this.populateActiveEventAndSetProvider(response.responseObject);
          this.getFlexibleBenefits(
            0,
            INITIAL_LOAD_CNT,
            this.eventId
          ).pipe(takeUntil(this.ngUnsubscribe)).subscribe({
            next: (data) => {
              this.onGetFlexibleBenefitsSuccess(data);

              this.dataLoadingDone = true;
              this.restoreScroll();
            },
            error: (error) => {
              this.onFlexibleBenefitsFail(error);
              this.dataLoadingDone = true;
            }
          })
        },
        error: () => {
          this.dataLoadingDone = true;
        },
      });
  }

  // In event mode benefits come from different providers, so we accept an optional providerId
  public goToOtherOffer(benefitId: number, resolvedProviderId?: number) {
    const pid = resolvedProviderId ?? this.providerId;
    this.router.navigate([
      '/' + this.subdomain + '/flexible-benefits-info/' + pid + '/' + benefitId,
    ]);
  }

  private populateActiveEventAndSetProvider(response: EventModel) {
    this.activeEvent = response;
    if (!this.activeEvent) {
      return;
    }

    // Synthetic provider so the existing template header renders the event name
    this.provider = {
      id: 0,
      name: this.activeEvent.name,
      description: this.activeEvent.description,
      email: null,
      fee: 0,
      isDeleted: false,
      isActive: true,
    } as Provider;
  }

  // ─── Provider-related methods ─────────────────────────────────────────────────────

  getProviderById(id: number) {
    return this.benefitDetailsService.getProviderById(id)
      .pipe(takeUntil(this.ngUnsubscribe))
  }

  onGetProviderByIdSuccess(data: GetProviderResponse) {
    this.provider = data.responseObject;
    this.provider.name = GetProviderName(this.provider.name, this.subdomain);

    // Custom description for 3Pillar subdomain
    if (this.subdomain === '3pillar') {
      this.provider.description = THREE_PILLAR_CUSTOM_DESCRIPTION;
    }

    this.checkAndGoTo24PayBenefit();
  }

  onGetProviderByIdFail(error) {}

  // ─── Benefits-related methods ────────────────────────────────────────────────

  public getFlexibleBenefits(start: number = 0, limit: number = INITIAL_LOAD_CNT,
                             eventId: number | undefined = undefined) {
    let params: GetFlexibleBenefitsRequest = {
      paginationCriteria: {
        start: start,
        limit: limit
      },
      companyId: this.companyId,
      providerId: !isNaN(this.providerId) ? this.providerId : 0,
      orderProperty: 'Name',
      isAscending: true,
      isBusinessProduct: false,
      eventId: eventId
    };

    params = this.populateParamsWithFilters(params);

    return this.benefitsService
      .getUserFlexibleBenefits(params)
      .pipe(takeUntil(this.ngUnsubscribe))
  }

  public getFlexibleBenefitsWithSubscription(start: number = 0, limit: number = INITIAL_LOAD_CNT,
                                             eventId: number | undefined = undefined) {
    const req = this.getFlexibleBenefits(start, limit, eventId);
    req.subscribe({
      next: (data) => {
        this.onGetFlexibleBenefitsSuccess(data);
      },
      error: (error) => {
        this.onFlexibleBenefitsFail(error);
      }
    });
  }

  public onGetFlexibleBenefitsSuccess(data: GetFlexibleBenefitsResponse) {
    const receivedBenefits = data.responseObject.data
      .map(b => Object.assign(new FlexibleBenefit(), b));
    this.loadedBenefits = [...this.loadedBenefits, ...receivedBenefits];
    this.totalBenefits = data.responseObject.totalResults;
    this.benefitsOutdated = false;
    this.checkAndGoTo24PayBenefit();

    // Send Google Analytics event for the current page of loaded benefits (pagination-aware)
    // Only send for the benefits received in this page, not all loadedBenefits, to avoid duplicates.
    //
    // TODO: Check if the proposed approach is correct
    if (receivedBenefits && receivedBenefits.length > 0) {
      const googleAnalyticsBenefits = receivedBenefits.map((b) => ({
        item_id: b.id,
        item_sku: b.sku,
        item_name: b.name,
        item_category: b.categoryName,
        item_category_id: b.categoryId,
        item_brand: b.providerName,
        price: b.price,
        affiliation: b.providerName,
        vat_included: b.includesVat,
        vat_percentage: b.vatPercentage,
        card_payment_allowed: b.hasCreditCardAllowed(),
        tax_type: b.benefitTaxCategory,
        supplier_id: b.providerId,
        benefit_Type: b.benefitTypeId,
        item_list_name: b.providerName,
        item_list_id: b.providerId,
      }));
      if (googleAnalyticsBenefits.length > 0) {
        this.gAnalyticsService.addGeneralEventsAnalytics('event', 'view_item_list', {
          item_list_name: googleAnalyticsBenefits[0].item_list_name,
          item_list_id: googleAnalyticsBenefits[0].item_list_id,
          items: googleAnalyticsBenefits
        });
      }
    }
  }

  public onFlexibleBenefitsFail(error: HttpErrorResponse) {
    this.benefitsOutdated = false;
  }

  public checkAndGoTo24PayBenefit() {
    if(this.loadedBenefits.length > 0 &&
      this.provider != null &&
      this.provider.name.includes('24Pay'))
    {
      this.goToOtherOffer(this.loadedBenefits[0].id);
    } else {
      this.setBenefits();
    }
  }

  public setBenefits() {
    if (this.loadedBenefits.length === 0) {
      this.cards = [];
      return;
    }
    this.cards = this.loadedBenefits
      .map((benefit: FlexibleBenefit) => this.benefitHelper.mapBenefitToDisplayPrice(benefit));
  }

  public loadMoreBenefits() {
    if (this.loadedBenefits && this.loadedBenefits.length === this.totalBenefits)
      return;

    const lim = Math.min(LOAD_MORE_CNT, this.totalBenefits - this.loadedBenefits.length);

    this.getFlexibleBenefitsWithSubscription(
      this.loadedBenefits.length,
      lim,
      this.isEventMode ? this.eventId : undefined
    );
  }

  // ─── Filters-related methods ────────────────────────────────────────────────

  private initAppliedFilters() {
    let hasInitAppliedFilters = false;
    const queryParams = this._route.snapshot.queryParams;

    const parseParam = (param: any): number[] => {
      if (Array.isArray(param)) {
        return param.map(Number);
      } else if (typeof param === 'string') {
        return param.split(',').map(v => Number(v.trim())).filter(v => !isNaN(v));
      } else if (typeof param === 'number') {
        return [param];
      }
      return [];
    };

    ['benefit_category', 'tax_category', 'budgets', 'locations'].forEach(key => {
      if (queryParams[key]) {
        const arr = parseParam(queryParams[key]);
        if (arr.length > 0) {
          this.unconfirmedFilters[key] = arr;
          this.appliedFilters[key] = arr;
          hasInitAppliedFilters = true;
        }
      }
    });

    return hasInitAppliedFilters;
  }

  public changeFilters(filters){
    this.unconfirmedFilters = {...this.unconfirmedFilters, ...filters};
    this.savedFilterScrollPosition = window.scrollY;
    this.shouldRestoreScroll = true;
    if (!this.initialBenefitsNotFiltered) {
      this.benefitsOutdated = true;
    } else {
      this.initialBenefitsNotFiltered = false;
    }
  }

  public applyFilters() {
    if (this.benefitsOutdated) {
      this.appliedFilters = {...this.unconfirmedFilters};
      this.resetScrollAndBenefits();
    }
  }

  resetAllFilters(){
    this.unconfirmedFilters = {};
    this.appliedFilters={};
    this.resetScrollAndBenefits();
  }

  private populateParamsWithFilters(params: GetFlexibleBenefitsRequest): GetFlexibleBenefitsRequest {
    if (!params.appliedFilters) {
      params.appliedFilters = {}
    }

    if(this.appliedFilters.benefit_category?.length > 0) {
      params.appliedFilters.categoryIds = this.appliedFilters.benefit_category;
    }
    if(this.appliedFilters.tax_category?.length > 0) {
      params.appliedFilters.benefitTaxCategories = this.appliedFilters.tax_category;
    }
    if(this.appliedFilters.locations?.length > 0) {
      params.appliedFilters.locations = this.appliedFilters.locations;
    }
    if(this.appliedFilters.budgets?.length > 0) {
      params.appliedFilters.benefitTypes = this.appliedFilters.budgets;
    }

    return params;
  }

  // Scroll-related methods

  private restoreScroll() {
    setTimeout(() => {
      window.scrollTo(0, FlexibleSupplierInfoComponent.savedScrollPosition);
    }, 0);
  }

  private resetScrollAndBenefits() {
    FlexibleSupplierInfoComponent.savedScrollPosition = 0;
    setTimeout(() => window.scrollTo(0, FlexibleSupplierInfoComponent.savedScrollPosition), 0);

    // Clear & re-fetch benefits for the current provider
    this.loadedBenefits = [];
    this.totalBenefits = 0;
    this.benefitsService.clearBenefitsCache(this.companyId, this.providerId);
    this.getFlexibleBenefitsWithSubscription(
      0,
      INITIAL_LOAD_CNT,
      this.isEventMode ? this.eventId : undefined
    );
  }

  protected readonly BenefitType = BenefitType;
}
